"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { FiX, FiMail, FiSend, FiPaperclip } from "react-icons/fi"
import toast from "react-hot-toast"
import api from "../../libs/axios"

interface IContactMessage {
    id: number
    name: string
    email: string | null
    subject: string | null
    message: string
    status: "unread" | "read" | "replied"
    created_at: Date
    phone_number?: string | null
    image?: string | null
    replied_at?: Date | null
    replied_by?: number | null
    reply_message?: string | null
    updated_at?: Date
    user_id?: number | null
    user?: { id: number; phone_number?: string | null; name?: string | null } | null
    images?: string[] | null
    replies?: { id: number; sender_type: "user" | "admin"; body: string; images?: string[] | null; created_at: Date }[]
}

interface ReplyMessageOffcanvasProps {
    messageData: IContactMessage
    showReplyCanvas: boolean
    setShowReplyCanvas: (show: boolean) => void
    fetchMessages: () => void
}

interface ReplyFormData {
    to: string
    message: string
}


const ReplyMessageOffcanvas: React.FC<ReplyMessageOffcanvasProps> = ({
    messageData,
    showReplyCanvas,
    setShowReplyCanvas,
    fetchMessages,
}) => {
    const [formData, setFormData] = useState<ReplyFormData>({
        to: messageData.phone_number || "",
        message: "",
    })
    const [fullMessageData, setFullMessageData] = useState<IContactMessage>(messageData)
    const [errors, setErrors] = useState<Partial<ReplyFormData>>({})
    const [loading, setLoading] = useState(false)
    const [fetchLoading, setFetchLoading] = useState(false)
    const [lightbox, setLightbox] = useState<string | null>(null)
    const [pendingImages, setPendingImages] = useState<File[]>([])
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Fetch full message details when component mounts
    useEffect(() => {
        const fetchMessageDetails = async () => {
            setFetchLoading(true)
            try {
                const response = await api.get(`/contact-messages/${messageData.id}`)
                setFullMessageData(response.data)


            } catch (error) {
                console.log(error)
                toast.error("Error fetching message details")
            } finally {
                setFetchLoading(false)
            }
        }

        fetchMessageDetails()
    }, [messageData.id])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))

        // Clear error when user starts typing
        if (errors[name as keyof ReplyFormData]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }))
        }
    }

    const validateForm = (): boolean => {
        const newErrors: Partial<ReplyFormData> = {}

        // Message validation (required)
        if (!formData.message.trim()) {
            newErrors.message = "Message is required"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (validateForm()) {
            setLoading(true)

            try {
                // Upload any attached images first (one per request; type=contact_images)
                const uploadedUrls: string[] = []
                for (const file of pendingImages) {
                    const fd = new FormData()
                    fd.append("files", file)
                    fd.append("type", "contact_images")
                    const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
                    const url = up?.data?.files?.[0]?.url
                    if (url) uploadedUrls.push(url)
                }

                await api.post(`/contact-messages/${messageData.id}/reply`, {
                    reply_message: formData.message,
                    images: uploadedUrls,
                })

                toast.success(`Reply sent to ${messageData.name} successfully!`)

                // Refresh the list + re-fetch THIS thread so the new reply appears (chat-like); keep panel open
                fetchMessages()
                try {
                    const refreshed = await api.get(`/contact-messages/${messageData.id}`)
                    setFullMessageData(refreshed.data)
                } catch (e) { /* keep panel open regardless */ }
                setFormData({ to: messageData.phone_number || "", message: "" })
                setErrors({})
                setPendingImages([])
            } catch (err: any) {
                console.log(err)
                toast.error(err?.response?.data?.message || "Error sending reply")
            } finally {
                setLoading(false)
            }
        } else {
            toast.error("Please fix the validation errors before sending")
        }
    }

    const handleClose = () => {
        setShowReplyCanvas(false)
        setErrors({})
    }

    const onPickImages = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        if (files.length) setPendingImages((prev) => [...prev, ...files].slice(0, 10))
        if (fileInputRef.current) fileInputRef.current.value = ""
    }
    const removePendingImage = (idx: number) => setPendingImages((prev) => prev.filter((_, i) => i !== idx))

    const formatDate = (date: Date | undefined | null) => {
        if (!date) return ""
        return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${showReplyCanvas ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${showReplyCanvas ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiMail className="mr-2" />
                        Reply to {fullMessageData.name}
                    </h2>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {fetchLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                            </div>
                        ) : (
                            <>
                                {/* Compact ticket header */}
                                <div className="bg-gray-50 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                                    <span className="font-semibold text-gray-800">{fullMessageData.user?.name || fullMessageData.name || "User"}</span>
                                    {fullMessageData.user_id ? (
                                        <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">User #{fullMessageData.user_id}</span>
                                    ) : (
                                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Guest</span>
                                    )}
                                    {fullMessageData.phone_number && <span>+91 {fullMessageData.phone_number}</span>}
                                    {fullMessageData.subject && <span className="italic">&ldquo;{fullMessageData.subject}&rdquo;</span>}
                                    <span className={`ml-auto px-2 py-1 capitalize text-xs rounded-full ${fullMessageData.status === "replied" ? "bg-purple-100 text-purple-800" : fullMessageData.status === "unread" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>{fullMessageData.status}</span>
                                </div>

                                {/* Opening message (from the user) */}
                                <div className="flex justify-start">
                                    <div className="max-w-[75%] p-3 rounded-2xl rounded-tl-sm text-sm whitespace-pre-wrap bg-gray-100 text-gray-800">
                                        <div className="text-[10px] font-semibold mb-1 uppercase text-gray-500">User &middot; {formatDate(fullMessageData.created_at)}</div>
                                        {fullMessageData.message}
                                        {(fullMessageData.images && fullMessageData.images.length ? fullMessageData.images : (fullMessageData.image ? [fullMessageData.image] : [])).length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {(fullMessageData.images && fullMessageData.images.length ? fullMessageData.images : [fullMessageData.image as string]).map((src, i) => (
                                                    <img key={i} src={src} onClick={() => setLightbox(src)} alt="" className="w-16 h-16 object-cover rounded border cursor-pointer" />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                                                {/* Conversation thread */}
                                {Array.isArray(fullMessageData.replies) && fullMessageData.replies.length > 0 ? (
                                    <div className="space-y-3">
                                        {fullMessageData.replies.map((r) => (
                                            <div key={r.id} className={`flex ${r.sender_type === "admin" ? "justify-end" : "justify-start"}`}>
                                                <div className={`max-w-[75%] p-3 rounded-lg text-sm whitespace-pre-wrap ${r.sender_type === "admin" ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-800"}`}>
                                                    <div className={`text-[10px] font-semibold mb-1 uppercase ${r.sender_type === "admin" ? "text-white/80" : "text-gray-500"}`}>
                                                        {r.sender_type === "admin" ? "Support" : "User"} · {formatDate(r.created_at)}
                                                    </div>
                                                    {r.body}
                                                    {Array.isArray(r.images) && r.images.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {r.images.map((src, i) => (
                                                                <img key={i} src={src} onClick={() => setLightbox(src)} alt="" className="w-16 h-16 object-cover rounded border cursor-pointer" />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : fullMessageData.reply_message ? (
                                    <div className="bg-purple-50 p-4 rounded-lg space-y-2">
                                        <h3 className="text-sm font-medium text-purple-700">Previous Reply:</h3>
                                        <div className="text-sm text-gray-600">
                                            <p>
                                                <strong>Replied At:</strong> {formatDate(fullMessageData.replied_at)}
                                            </p>
                                        </div>
                                        <div className="mt-2 p-3 bg-white rounded border-l-4 border-purple-300">
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                {fullMessageData.reply_message}
                                            </p>
                                        </div>
                                    </div>
                                ) : null}

                            </>
                        )}
                    </div>

                    {/* Chat composer */}
                    <div className="border-t border-gray-200 p-4 mb-20">
                        {pendingImages.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {pendingImages.map((f, i) => (
                                    <div key={i} className="relative">
                                        <img src={URL.createObjectURL(f)} alt="" className="w-14 h-14 object-cover rounded border" />
                                        <button type="button" onClick={() => removePendingImage(i)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex items-end gap-2">
                            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickImages} />
                            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={pendingImages.length >= 10} title="Attach photos (up to 10)"
                                className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                                <FiPaperclip />
                            </button>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                rows={2}
                                className={`flex-1 resize-none px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.message ? "border-red-500" : "border-gray-300"}`}
                                placeholder="Type a reply…"
                            />
                            <button type="button" onClick={handleSubmit} disabled={loading || fetchLoading}
                                className="bg-primary-600 text-white h-10 px-4 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors font-medium flex items-center justify-center disabled:opacity-50">
                                {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <FiSend />}
                            </button>
                        </div>
                        {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                    </div>
                </div>
            </div>
            {lightbox && (
                <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
                    <img src={lightbox} alt="attachment" className="max-h-full max-w-full rounded shadow-lg" onClick={(e) => e.stopPropagation()} />
                    <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white text-4xl leading-none">&times;</button>
                </div>
            )}
        </>
    )
}

export default ReplyMessageOffcanvas